/*
 * Authors: mariushirjanu
 * Created: 17/08/2021
 */
import { HttpDataService, IHttpDataCache } from '../../http/http-data.service';

import { ICompanyCountryExtended, IContact, ICountry, ICurrency, ILocationExtended } from '../../../../type_definitions/datatypes';
import { Injectable } from '@angular/core';

export interface IInvoiceTemplate {
  paymentComment?:string;
  withholdingTaxPct?:number;
  id:number;
  companyCountryId:number;
  direction:string;
  type:string;
  companyCountry:ICompanyCountryExtended;
  contact:IContact;
  location:ILocationExtended;
  acslCompanyCountry:ICompanyCountryExtended;
  acslLocation:ILocationExtended;
  currency:ICurrency;
  minimumAmount:number;
  paymentTerms:number;
  subject:string;
  bankAccount?:{
    bankId:number;
    bankName:string;
    accountNumber:string;
    country:ICountry;
  };
}

export interface IStoredInvoiceTemplateDetail {
  id:number;
  companyCountry:ICompanyCountryExtended;
}

export interface IInvoiceTemplateDetail extends IInvoiceTemplate {
  acslContact:IContact;
}

export interface IInvoiceCompanyCountry extends ICompanyCountryExtended {
  invoiceTemplate?:{
    id:number;
    direction:string;
  };
}

@Injectable()
export class InvoiceTemplatesDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  // returns the 'owned' templates
  public invoiceTemplateDetail(invoiceTemplateId:number):Promise<IInvoiceTemplateDetail> {
    return this.httpDataService.doGET(
      `rest/invoicetemplate/invoicetemplates/${invoiceTemplateId}`,
      undefined,
      undefined,
      'Error loading invoice template detail'
    );
  }

  // returns the 'used' template for specified direction
  public usedInvoiceTemplateDetail(companyCountryId:number, direction:string):Promise<IInvoiceTemplateDetail> {
    return this.httpDataService.doGET(
      'rest/invoicetemplate/usedinvoicetemplate',
      {
        companyCountryId,
        direction
      },
      'Loading invoice template',
      'Error loading invoice template detail'
    );
  }

  public invoiceTemplates(companyCountryId:number):Promise<IInvoiceTemplate[]> {
    return this.httpDataService.doGET(
      'rest/invoicetemplate/invoicetemplates',
      { companyCountryId },
      'Loading invoice templates',
      'Error loading invoice templates'
    );
  }

  public saveInvoiceTemplateDetail(invoiceTemplateId:number, data:IInvoiceTemplate):Promise<IInvoiceTemplateDetail> {
    return this.httpDataService.doPUT(
      `rest/invoicetemplate/invoicetemplates/${invoiceTemplateId}`,
      undefined,
      data,
      'Saving invoice template',
      'Error saving invoice template'
    );
  }

  // allows only to update companyCountry.id everything else is ignored
  public updateInvoiceTemplateDetail(invoiceTemplateId:number, data:IStoredInvoiceTemplateDetail):Promise<IInvoiceTemplateDetail> {
    return this.httpDataService.doPATCH(
      `rest/invoicetemplate/invoicetemplates/${invoiceTemplateId}`,
      undefined,
      data,
      'Saving invoice template',
      'Error updating invoice template'
    );
  }

  public companyCountries(companyCountryId:number, direction:string):Promise<IInvoiceCompanyCountry[]> {
    return this.cache.doGET(
      'rest/invoicetemplate/invoicecompanycountries',
      {
        companyCountryId,
        direction
      },
      undefined,
      'Error loading company countries'
    );
  }

  public locations(companyCountryId:number):Promise<ILocationExtended[]> {
    return this.cache.doGET(
      `rest/invoicetemplate/invoicecompanycountries/${companyCountryId}/locations`,
      undefined,
      undefined,
      'Error loading company country locations'
    );
  }

  public acslLocations(companyCountryId:number):Promise<ILocationExtended[]> {
    return this.cache.doGET(
      `rest/acslinfo/companycountries/${companyCountryId}/locations`,
      undefined,
      undefined,
      'Error loading company country ACSL locations'
    );
  }

  public clear():void {
    this.cache.clear();
  }
}